/* eslint-disable no-console */

/* eslint-disable indent */
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SingleValue } from 'react-select';

import * as signalR from '@microsoft/signalr';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Formik, FormikProvider, useFormik } from 'formik';

import { DateInput, Input, Modal, SingleSelect } from '@/common';
import { Button, IconButton, Label, LABEL_TYPE, Spinner, ToggleSwitch } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { IconButtonVariant } from '@/components/generic/IconButton/icon-button-config';
import { ClickOutsideWrapper } from '@/components/layouts/ClickOutsideWrapper';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal';
import { COVER_PHOTO, dateAndHoursFormat, UPLOAD_PICKER_TYPES } from '@/constants';
import { AuthContext, EventContext } from '@/contexts';
import {
  AssetScope,
  AssetType,
  LiveStreamModalType,
  PERMISSION_TYPE,
  STREAM_STATUS
} from '@/enums';
import { ORGANIZATION_PERMISSION_TYPE } from '@/enums/OrganizationPermissionEnum';
import {
  buttonTypeMapping,
  getAssetTagName,
  getDescriptionMapping,
  getLabelMapping,
  getUploadOptions,
  webSocketApiUrl
} from '@/helpers';
import { formatDate, formatDateWithTranslation } from '@/helpers/date';
import { showToast, ToastType } from '@/helpers/toast';
import { useFileUpload, useUploadFilePicker } from '@/hooks';
import { InvitationItem } from '@/pages/Event/LiveStream/InvitationItem';
import { LiveStreamModalContent } from '@/pages/Event/LiveStream/LiveStreamModalContent';
import {
  liveStreamDescriptionSchema,
  liveStreamSchema,
  recordingDateSchema
} from '@/pages/Event/LiveStream/LiveStreamValidation';
import { WaitingScreen } from '@/pages/Event/LiveStream/WaitingScreen';
import {
  BlueCheckIcon,
  EditPenSmallIcon,
  LinkIcon,
  LiveStreamIcon,
  RestartIcon,
  StartStreamIcon,
  StopStreamIcon
} from '@/resources';
import {
  useCreateStreamEventMutation,
  useCreateStreamInvitationMutation,
  useDeleteStreamInvitationMutation,
  useGetAllStreamInvitations,
  useGetEventAssets,
  useGetEventTags,
  useGetGuestLiveStreamLinkQuery,
  useGetPublicLiveStreamLinkQuery,
  useUpdateStreamEvent,
  useUpdateStreamStatus
} from '@/services';
import {
  Asset,
  SelectOptionProps,
  Tag,
  UpdateStreamEventPayload,
  UpdateStreamStatus
} from '@/types';
import { TestIds } from '@/shared/test-ids';

type LiveStreamProps = {
  isMobileView?: boolean;
  organizationPermissions?: string[];
};

const LIVE_STREAM_DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  month: 'long',
  day: 'numeric',
  year: 'numeric'
};

const LIVE_STREAM_TIME_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false
};

export const LiveStream = ({
  isMobileView = false,
  organizationPermissions: eventOrganizationPermissions
}: LiveStreamProps) => {
  const { t, i18n } = useTranslation('common');
  const currentLanguage = i18n.language;
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [selectedInvitationId, setSelectedInvitationId] = useState<number | null>(null);
  const [deletingInvitation, setDeletingInvitation] = useState<{ id: number; name: string } | null>(
    null
  );
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState<Record<number, boolean>>({});
  const [isTooltipVisible, setIsTooltipVisible] = useState<Record<number, boolean>>({});
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedAsset, setSelectedAsset] = useState<Asset | undefined>();
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [openedModal, setOpenedModal] = useState<LiveStreamModalType | null>(null);
  const selectRef = useRef<HTMLDivElement>(null);
  const [description, setDescription] = useState<string>('');
  const [toggled, setToggled] = useState<boolean>(false);
  const [showRestartStreamModal, setShowRestartStreamModal] = useState<boolean>(false);
  const { eventData, isEventLoading, refetch } = useContext(EventContext);
  const { permissions } = useContext(AuthContext);
  const streamEvent = eventData?.streamEvent;
  const [streamStarted, setStreamStarted] = useState<boolean>(
    streamEvent?.streamStatus ? streamEvent.streamStatus === STREAM_STATUS.STARTED : false
  );
  const disabledRestartButton = streamEvent?.streamStatus
    ? streamEvent?.streamStatus === STREAM_STATUS.NOT_STARTED
    : true;
  const [streamEnded, setStreamEnded] = useState<boolean>(
    streamEvent?.streamStatus ? streamEvent.streamStatus === STREAM_STATUS.ENDED : false
  );
  const [streamId, setStreamId] = useState<string>(streamEvent?.streamId || '');
  const [streamIdError, setStreamIdError] = useState<boolean>(false);
  const hasChangeStreamStatusAccess = permissions?.includes(PERMISSION_TYPE.CHANGE_STREAM_STATUS);
  const [isRecordingEnabled, setIsRecordingEnabled] = useState<boolean>(false);
  const [recordingDate, setRecordingDate] = useState<Date | null>(null);
  const { organizationPermissions: userOrganizationPermissions } = useContext(AuthContext);
  const organizationPermissions = eventOrganizationPermissions ?? userOrganizationPermissions;
  const { currentUserData: currentUser } = useContext(AuthContext);
  const hasOrgAccess = currentUser?.role.permissions.includes(PERMISSION_TYPE.ORG_ACCESS);

  const hasLiveStreamingAccess = organizationPermissions.includes(
    ORGANIZATION_PERMISSION_TYPE.LIVE_STREAMING
  );
  const hasContentManagementAccess = organizationPermissions.includes(
    ORGANIZATION_PERMISSION_TYPE.CONTENT_MANAGEMENT
  );
  const hasCoverPhotoCreationAccess = organizationPermissions.includes(
    ORGANIZATION_PERMISSION_TYPE.COVER_PHOTO_CREATION
  );

  const { data: allAssets, isLoading: isLoadingAllAssets } = useGetEventAssets(
    eventData?.id ?? '',
    [],
    0,
    undefined,
    [AssetType.IMAGE.toString()],
    undefined,
    undefined,
    hasContentManagementAccess
  );

  const { mutate: updateStreamStatus } = useUpdateStreamStatus(queryClient, {
    onSuccess: () => {
      showToast(ToastType.SUCCESS, t('streamStatusUpdated'));
    },
    onError: (error) => {
      showToast(ToastType.ERROR, error.response.data.ErrorMessages[0].DisplayMessage);
    }
  });

  const { data: allTags, isLoading: isLoadingTags } = useGetEventTags(eventData?.id ?? '');

  const { data: allCoverPhotos, isLoading: isLoadingCoverPhotos } = useGetEventAssets(
    eventData?.id ?? '',
    allTags?.data?.filter((tag: Tag) => tag.name === COVER_PHOTO).map((tag: Tag) => `${tag.id}`) ??
      [],
    0,
    '',
    [AssetType.COVER_PHOTO.toString()],
    undefined,
    true,
    !isLoadingTags && hasContentManagementAccess
  );

  const { data: publicLiveStreamLink } = useGetPublicLiveStreamLinkQuery(
    Number(streamEvent?.id),
    hasLiveStreamingAccess
  );

  const { data: allStreamInvitations, isLoading: isLoadingAllStreamInvitations } =
    useGetAllStreamInvitations(0, Number(streamEvent?.id), false, hasLiveStreamingAccess);

  const { data: guestStreamInvitation } = useGetGuestLiveStreamLinkQuery(
    Number(selectedInvitationId),
    hasLiveStreamingAccess
  );

  const { mutate: createStreamInvitation } = useCreateStreamInvitationMutation(queryClient, {
    onSuccess: () => {
      setIsSubmitting(false);
      formik.resetForm();
    },
    onError: (error) => {
      showToast(ToastType.ERROR, error.response.data.ErrorMessages[0].DisplayMessage);
      setIsSubmitting(false);
    }
  });

  const { mutate: deleteInvitation } = useDeleteStreamInvitationMutation(queryClient, {
    onSuccess: () => {
      showToast(ToastType.SUCCESS, t('invitationSuccessfullyDeleted'));
    },
    onError: () => {
      showToast(ToastType.ERROR, t('invitationDeleteError'));
    }
  });

  const { mutate: createStreamEvent } = useCreateStreamEventMutation(queryClient, {
    onSuccess: () => {
      showToast(ToastType.SUCCESS, t('streamEventSuccessfullyCreated'));
    },
    onError: (error) => {
      showToast(ToastType.ERROR, error.response.data.ErrorMessages[0].DisplayMessage);
    }
  });

  const { mutate: updateStreamEvent } = useUpdateStreamEvent(queryClient, {
    onSuccess: () => {
      showToast(ToastType.SUCCESS, t('streamEventSuccessfullyUpdated'));
    },
    onError: (error) => {
      showToast(ToastType.ERROR, error.response.data.ErrorMessages[0].DisplayMessage);
    }
  });

  const { isUploading, uploadFiles } = useFileUpload({
    pickerTypes: UPLOAD_PICKER_TYPES.IMAGE,
    eventId: eventData?.id,
    organizationId: eventData?.organization?.id,
    assetScope: AssetScope.PRIVATE,
    assetTag: getAssetTagName(AssetType.IMAGE),
    maxFiles: 1
  });

  const { openFilePicker, hiddenInputProps } = useUploadFilePicker({
    pickerTypes: UPLOAD_PICKER_TYPES.IMAGE,
    multiple: false,
    disabled: isUploading,
    onFilesSelected: uploadFiles
  });

  useEffect(() => {
    const handleUploadComplete = (event: CustomEvent<{ id: number }>) => {
      setIsMenuOpen(false);
      setOpenedModal(null);
      const payload = {
        id: Number(streamEvent?.id),
        landingAssetId: event.detail.id,
        description: streamEvent?.description,
        enabled: streamEvent?.enabled,
        streamUrl: streamEvent?.streamUrl ?? null,
        isRecordingEnabled: streamEvent?.isRecordingEnabled,
        recordingDate: streamEvent?.recordingDate
      };
      updateStreamEvent(payload as UpdateStreamEventPayload);
    };

    document.addEventListener('uploadComplete', handleUploadComplete as EventListener);

    return () => {
      document.removeEventListener('uploadComplete', handleUploadComplete as EventListener);
    };
  }, [selectRef, streamEvent]);

  const onDateInputClose = () => {
    if (streamEvent) {
      const payload = {
        id: Number(streamEvent?.id),
        enabled: streamEvent?.enabled,
        recordingDate: recordingDate,
        landingAssetId: streamEvent?.landingAsset?.id,
        description: streamEvent?.description,
        isRecordingEnabled: streamEvent.isRecordingEnabled
      };
      updateStreamEvent(payload);
    }
  };

  useEffect(() => {
    if (guestStreamInvitation) {
      setIsTooltipVisible((prevState) => ({
        ...prevState,
        [Number(selectedInvitationId)]: true
      }));
    }
  }, [guestStreamInvitation]);

  useEffect(() => {
    if (eventData?.startDateAndTime && !eventData?.streamEvent?.description) {
      const translatedDescription = t('waitingForLiveStream', {
        date: formatDateWithTranslation(
          new Date(eventData.startDateAndTime),
          t,
          LIVE_STREAM_DATE_FORMAT_OPTIONS
        ),
        time: formatDate(new Date(eventData.startDateAndTime), LIVE_STREAM_TIME_FORMAT_OPTIONS)
      });
      setDescription(translatedDescription);
    } else if (eventData?.streamEvent?.description) {
      setDescription(eventData?.streamEvent?.description);
    }
    setToggled(eventData?.streamEvent?.enabled as boolean);
    setIsRecordingEnabled(eventData?.streamEvent?.isRecordingEnabled as boolean);
    setRecordingDate(eventData?.streamEvent?.recordingDate ?? null);
  }, [eventData, t, currentLanguage]);

  useEffect(() => {
    if (streamEvent) {
      setStreamStarted(streamEvent.streamStatus === STREAM_STATUS.STARTED);
      setStreamEnded(streamEvent.streamStatus === STREAM_STATUS.ENDED);
      setStreamId((prevStreamId) =>
        prevStreamId === '' ? streamEvent.streamId || '' : prevStreamId
      );
    }

    if (!streamEvent || streamEvent.streamStatus === STREAM_STATUS.NOT_STARTED) {
      setStreamId((prevStreamId) =>
        prevStreamId === '' ? eventData?.device?.streamId || '' : prevStreamId
      );
    }
  }, [streamEvent]);

  const dataMapping = useMemo<Record<number, Asset[]>>(() => {
    return {
      [LiveStreamModalType.UPLOADED_CONTENT]: allAssets?.pages.flatMap(
        (page) => page.data?.items
      ) as Asset[],
      [LiveStreamModalType.COVER_PHOTO]: allCoverPhotos?.pages.flatMap(
        (page) => page.data?.items
      ) as Asset[]
    };
  }, [allAssets, allCoverPhotos]);

  const streamInvitations = useMemo(() => {
    return allStreamInvitations?.pages.flatMap((page) => page.data?.items) || [];
  }, [allStreamInvitations]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: ''
    },
    validationSchema: liveStreamSchema,
    onSubmit: (values) => {
      setIsSubmitting(true);
      const payload = {
        email: values.email,
        streamEventId: Number(streamEvent?.id)
      };
      createStreamInvitation(payload);
    }
  });

  const roundMinutes = (date: Date) => {
    date.setHours(date.getHours() + Math.round(date.getMinutes() / 60));
    date.setMinutes(0, 0, 0);

    return date;
  };

  const formikRecording = useFormik({
    enableReinitialize: true,
    initialValues: {
      recordingDate: streamEvent?.recordingDate
        ? roundMinutes(new Date(streamEvent?.recordingDate))
        : roundMinutes(new Date())
    },
    validationSchema: recordingDateSchema,
    onSubmit: () => undefined
  });

  const formikDescription = useFormik({
    enableReinitialize: true,
    initialValues: {
      description: description
    },
    validationSchema: liveStreamDescriptionSchema,
    onSubmit: () => {
      setIsSubmitting(true);
    }
  });

  const handleGetGuestStreamLink = (invitationId: number) => {
    setSelectedInvitationId(invitationId);
    selectedInvitationId === invitationId &&
      setIsTooltipVisible((prevState) => ({
        ...prevState,
        [Number(selectedInvitationId)]: true
      }));
  };

  const handleOpenDeleteModal = (invitationId: number, invitationEmail: string) => {
    setIsOpenDeleteModal((prevState) => ({
      ...prevState,
      [Number(invitationId)]: true
    }));
    setDeletingInvitation({ id: invitationId, name: invitationEmail });
  };

  const handleTooltipClose = (id: number) => {
    setSelectedInvitationId(null);
    setIsTooltipVisible((prevState) => ({
      ...prevState,
      [id]: false
    }));
  };

  const handleChange = (selectedOption: SingleValue<SelectOptionProps>) => {
    if (selectedOption?.value === LiveStreamModalType.UPLOAD_NEW) {
      openFilePicker();
    } else {
      setIsModalOpen(true);
      setOpenedModal(Number(selectedOption?.value));
    }
    setIsMenuOpen(false);
  };

  const onCloseModal = () => {
    setIsModalOpen(false);
    setOpenedModal(null);
  };

  const handleOpenDescriptionModal = () => {
    setIsModalOpen(true);
    setOpenedModal(LiveStreamModalType.DESCRIPTION);
  };

  const handleToggle = () => {
    if (streamEvent?.enabled) {
      const payload = {
        id: Number(streamEvent?.id),
        landingAssetId: streamEvent?.landingAsset?.id,
        description: streamEvent?.description,
        enabled: false,
        streamUrl: streamEvent?.streamUrl ?? null
      };
      setToggled(false);
      updateStreamEvent(payload as UpdateStreamEventPayload);
    } else {
      setIsModalOpen(true);
      setOpenedModal(LiveStreamModalType.TURN_ON_LIVESTREAM);
    }
  };

  const handleImageClick = () => {
    setIsModalOpen(true);
    setOpenedModal(LiveStreamModalType.WAITING_SCREEN);
  };

  const handleCopyLiveStreamURL = async () => {
    try {
      if ('clipboard' in navigator) {
        await navigator.clipboard.writeText(publicLiveStreamLink?.data);
        showToast(ToastType.SUCCESS, t('liveStreamURLSuccessfullyCopied'));
        return true;
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = publicLiveStreamLink?.data;
        document.body.appendChild(textArea);
        textArea.select();

        const success = document.execCommand('copy');
        document.body.removeChild(textArea);

        return success;
      }
    } catch (err) {
      console.error('Unable to copy to clipboard', err);
      return false;
    }
  };

  const handleSubmitModal = () => {
    switch (openedModal) {
      case LiveStreamModalType.TURN_ON_LIVESTREAM:
        handleTurnOnLivestream();
        break;
      case LiveStreamModalType.DESCRIPTION:
        handleDescriptionModal();
        break;
      default:
        handleOtherModals();
    }
    setSelectedAsset(undefined);
  };

  const handleTurnOnLivestream = () => {
    if (streamEvent) {
      const payload = {
        id: Number(streamEvent?.id),
        landingAssetId: streamEvent?.landingAsset?.id,
        description: streamEvent?.description,
        enabled: true,
        streamUrl: streamEvent?.streamUrl ?? null
      };
      updateStreamEvent(payload as UpdateStreamEventPayload);
    } else {
      const payload = {
        eventId: Number(eventData?.id),
        description: description
      };
      createStreamEvent(payload);
    }
    setToggled(true);
    setIsModalOpen(false);
    setOpenedModal(null);
  };

  const handleDescriptionModal = () => {
    if (formikDescription.isValid) {
      const payload = {
        id: Number(streamEvent?.id),
        landingAssetId: streamEvent?.landingAsset?.id,
        description: formikDescription.values.description,
        enabled: streamEvent?.enabled,
        streamUrl: streamEvent?.streamUrl as string
      };
      updateStreamEvent(payload);
      setIsModalOpen(false);
      setOpenedModal(null);
    }
  };

  const handleOtherModals = () => {
    const payload = {
      id: Number(eventData?.streamEvent?.id),
      landingAssetId: Number(selectedAsset?.id),
      description: description,
      enabled: streamEvent?.enabled,
      streamUrl: streamEvent?.streamUrl as string,
      isRecordingEnabled: streamEvent?.isRecordingEnabled,
      recordingDate: streamEvent?.recordingDate ?? new Date()
    };
    updateStreamEvent(payload);
    setIsModalOpen(false);
    setOpenedModal(null);
  };

  useEffect(() => {
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${webSocketApiUrl()}/notificationHub`, {
        skipNegotiation: true,
        transport: signalR.HttpTransportType.WebSockets
      })
      .build();

    connection.on('streamEventUpdated', () => {
      refetch();
    });
  }, []);

  const changeIsRecordingEnabled = () => {
    if (streamEvent) {
      const payload = {
        id: Number(streamEvent?.id),
        enabled: streamEvent?.enabled,
        isRecordingEnabled: !isRecordingEnabled,
        landingAssetId: streamEvent?.landingAsset?.id,
        description: streamEvent?.description,
        recordingDate: new Date()
      };
      updateStreamEvent(payload);
    }
    setIsRecordingEnabled(!isRecordingEnabled);
  };

  const handleStartStream = () => {
    if (!streamId || streamId.trim() === '') {
      setStreamIdError(true);
      return;
    }
    setStreamIdError(false);
    updateStreamStatus({
      streamEventId: Number(streamEvent?.id),
      payload: {
        streamId: streamId,
        streamStatus: streamStarted ? STREAM_STATUS.ENDED : STREAM_STATUS.STARTED
      } as UpdateStreamStatus
    });
    if (streamStarted) {
      setStreamId('');
    }
  };

  const openRestartStreamModal = () => {
    setShowRestartStreamModal(true);
  };

  const closeRestartStreamModal = () => {
    setShowRestartStreamModal(false);
  };

  const handleRestartStream = () => {
    updateStreamStatus({
      streamEventId: Number(streamEvent?.id),
      payload: {
        streamStatus: STREAM_STATUS.NOT_STARTED
      } as UpdateStreamStatus
    });
    setStreamId('');
  };

  return (
    <>
      {(hasContentManagementAccess && (isLoadingAllAssets || isLoadingCoverPhotos)) ||
      isLoadingTags ||
      isEventLoading ||
      (streamEvent && hasLiveStreamingAccess && isLoadingAllStreamInvitations) ? (
        <div className='flex h-screen items-center justify-center'>
          <Spinner />
        </div>
      ) : (
        <div
          data-testid={TestIds.liveStream.page}
          className={`no-scrollbar size-full overflow-y-scroll md:scrollbar-none ${hasLiveStreamingAccess ? '' : 'relative'}`}>
          <input {...hiddenInputProps} />
          <div className='mb-10 md:mt-9'>
            <div className='flex size-full flex-col md:mx-20 md:w-auto md:flex-row md:gap-x-12'>
              <div className='flex size-full flex-col'>
                {!isMobileView && (
                  <div
                    data-testid={TestIds.liveStream.panelDesktop}
                    className='mb-6 mt-1 flex flex-col rounded-lg md:mb-0'>
                    <div className='flex flex-row justify-between'>
                      <Label text={t('liveStream')} type={LABEL_TYPE.H4_BOLD} className='my-5' />
                      <ToggleSwitch
                        data-testid={TestIds.liveStream.enabledToggle}
                        containerClass='mt-5 '
                        handleToggle={handleToggle}
                        toggled={toggled}
                      />
                    </div>
                    <div className='mb-5 flex flex-col gap-4 md:flex-row md:justify-between'>
                      <Button
                        dataTestId={TestIds.liveStream.visitButton}
                        variant={ButtonVariant.SECONDARY}
                        handleOnClick={() => window.open(publicLiveStreamLink?.data, '_blank')}
                        disabled={!streamEvent?.enabled}
                        className={`w-full gap-2 p-1 ${
                          !streamEvent?.enabled &&
                          'border border-secondary-steel-blue !font-medium text-secondary-steel-blue'
                        }`}>
                        <LiveStreamIcon
                          className={`size-5 ${
                            !streamEvent?.enabled && '!font-medium text-secondary-steel-blue'
                          }`}
                        />
                        {t('visitLiveStream')}
                      </Button>
                    </div>
                  </div>
                )}
                {hasChangeStreamStatusAccess && (
                  <div
                    data-testid={TestIds.liveStream.streamControls}
                    className='relative mb-4 rounded-lg'>
                    {(!streamEvent || !streamEvent?.enabled) && !isMobileView && (
                      <div className='absolute inset-0 z-10 rounded-lg backdrop-blur-[3px]' />
                    )}

                    <div className='flex gap-3 rounded-lg bg-primary-gray p-2'>
                      <div className='flex-1'>
                        <Input
                          dataTestId={TestIds.liveStream.streamIdInput}
                          placeholder={t('streamId')}
                          className={clsx(
                            'h-10',
                            streamIdError && '!border-accent-red',
                            streamEnded && '!bg-primary-gray'
                          )}
                          id='streamId'
                          type='text'
                          label=''
                          handleChange={(value: string) => {
                            setStreamId(value);
                            setStreamIdError(false);
                          }}
                          value={streamId}
                          disabled={streamEnded}
                        />
                      </div>

                      {streamEnded ? (
                        <IconButton
                          dataTestId={TestIds.liveStream.streamStartStopButton}
                          className='!border-accent-green !bg-second-accent-light-green'
                          variant={IconButtonVariant.PRIMARY}
                          handleOnClick={undefined}
                          disabled={true}
                          tooltip={t('streamFinished')}>
                          <BlueCheckIcon className='size-5 text-accent-green' />
                        </IconButton>
                      ) : streamStarted ? (
                        <IconButton
                          dataTestId={TestIds.liveStream.streamStartStopButton}
                          className='!border-accent-red'
                          variant={IconButtonVariant.PRIMARY}
                          handleOnClick={handleStartStream}
                          tooltip={t('endStream')}>
                          <StopStreamIcon
                            className={clsx('size-5 fill-accent-red', {
                              'opacity-50': disabledRestartButton
                            })}
                          />
                        </IconButton>
                      ) : (
                        <IconButton
                          dataTestId={TestIds.liveStream.streamStartStopButton}
                          variant={IconButtonVariant.PRIMARY}
                          handleOnClick={handleStartStream}
                          tooltip={t('startStream')}>
                          <StartStreamIcon className='size-5 fill-primary-blue' />
                        </IconButton>
                      )}
                      <IconButton
                        dataTestId={TestIds.liveStream.streamResetButton}
                        variant={IconButtonVariant.PRIMARY}
                        handleOnClick={openRestartStreamModal}
                        disabled={disabledRestartButton}
                        tooltip={t('resetStream')}>
                        <RestartIcon
                          className={clsx('size-5 fill-primary-blue', {
                            'opacity-50': disabledRestartButton
                          })}
                        />
                      </IconButton>
                    </div>
                  </div>
                )}

                <div
                  data-testid={TestIds.liveStream.shareSection}
                  className={`relative ${!hasOrgAccess && 'mx-auto w-full max-w-3xl px-16'}`}>
                  <div
                    className={`${
                      (!streamEvent || !streamEvent?.enabled) &&
                      !isMobileView &&
                      'absolute inset-0 z-10 rounded-2xl backdrop-blur-[3px]'
                    }`}
                  />
                  <div className='md:mx-1'>
                    <div className='mb-4 flex flex-row justify-between'>
                      {(!isMobileView || !hasOrgAccess) && (
                        <Label
                          text={t('shareLivestream')}
                          type={LABEL_TYPE.H4_BOLD}
                          className={`${isMobileView && '!text-primary-black'}`}
                        />
                      )}
                      {hasOrgAccess && (
                        <div
                          data-testid={TestIds.liveStream.copyPublicUrlButton}
                          className={`flex cursor-pointer flex-row items-center border-none text-accent-dark-gray ${!streamEvent?.enabled && '!hover:shadow-none cursor-auto border-secondary-steel-blue !shadow-none'}`}
                          onClick={handleCopyLiveStreamURL}>
                          <LinkIcon className={'mr-2 w-5'} />
                          {t('copyURL')}
                        </div>
                      )}
                    </div>
                    <Label
                      text={t('pleaseEnterTheEmailAddress')}
                      type={LABEL_TYPE.PARAGRAPH2}
                      className={'mb-3 font-normal text-secondary-gray md:mb-4'}
                    />
                    <FormikProvider value={formik}>
                      <form data-testid={TestIds.liveStream.inviteForm} onSubmit={formik.handleSubmit}>
                        <div
                          className={`${
                            !isMobileView && 'flex flex-row items-center justify-between'
                          }`}>
                          <div className='w-full'>
                            <Input
                              dataTestId={TestIds.liveStream.inviteEmailInput}
                              type='text'
                              id='email'
                              label={''}
                              placeholder={t('enterEmail')}
                              className={`${isMobileView && 'h-9'}`}
                              errorMessage={formik.errors.email}
                              isInvalid={!!formik.errors.email && formik.touched.email}
                            />
                          </div>
                          <Button
                            dataTestId={TestIds.liveStream.inviteSubmitButton}
                            type='submit'
                            variant={ButtonVariant.PRIMARY}
                            disabled={isSubmitting || !(formik.isValid && formik.dirty)}
                            className={`text-sm ${isMobileView ? 'mt-1 w-full' : 'ml-5 px-9'}`}>
                            {t('invite')}
                          </Button>
                        </div>
                      </form>
                    </FormikProvider>
                  </div>
                  <div className='mb-1 mt-5 md:mx-1'>
                    {!isMobileView && (
                      <div
                        data-testid={TestIds.liveStream.guestListDesktop}
                        className='mt-4 flex max-h-60 w-full flex-col rounded-lg border border-solid border-secondary-blue-gray pl-6 pr-3 3xl:max-h-80 4xl:max-h-80'>
                        <Label
                          text={t('guestsForLiveStream')}
                          type={LABEL_TYPE.H4_BOLD}
                          className='mt-5'
                        />
                        <div className='custom-scrollbar mb-5 overflow-y-auto pt-5'>
                          <ul>
                            {streamInvitations?.map((invitation, index) => (
                              <InvitationItem
                                key={invitation.id}
                                invitation={invitation}
                                guestStreamInvitationData={guestStreamInvitation?.data || null}
                                isTooltipVisible={isTooltipVisible}
                                handleGetGuestStreamLink={handleGetGuestStreamLink}
                                handleTooltipClose={handleTooltipClose}
                                selectedInvitationId={selectedInvitationId}
                                handleOpenDeleteModal={handleOpenDeleteModal}
                                deleteInvitation={deleteInvitation}
                                isOpenDeleteModal={isOpenDeleteModal}
                                setIsOpenDeleteModal={setIsOpenDeleteModal}
                                deletingInvitation={deletingInvitation}
                                isLastItem={index === streamInvitations.length - 1}
                              />
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                  {isMobileView && (
                    <>
                      <div className='mb-5 mt-6 border-b border-solid border-secondary-blue-gray' />
                      <Label
                        text={t('guestsForLiveStream')}
                        type={LABEL_TYPE.H4_BOLD}
                        className='mb-2 !text-primary-black'
                      />
                      <ul data-testid={TestIds.liveStream.guestListMobile}>
                        {streamInvitations?.map((invitation) => (
                          <InvitationItem
                            key={invitation.id}
                            invitation={invitation}
                            guestStreamInvitationData={guestStreamInvitation?.data || null}
                            isTooltipVisible={isTooltipVisible}
                            handleGetGuestStreamLink={handleGetGuestStreamLink}
                            handleTooltipClose={handleTooltipClose}
                            selectedInvitationId={selectedInvitationId}
                            handleOpenDeleteModal={handleOpenDeleteModal}
                            deleteInvitation={deleteInvitation}
                            isOpenDeleteModal={isOpenDeleteModal}
                            setIsOpenDeleteModal={setIsOpenDeleteModal}
                            deletingInvitation={deletingInvitation}
                          />
                        ))}
                      </ul>
                      <div className='mb-5 mt-4 border-b border-solid border-secondary-blue-gray' />
                      <div
                        data-testid={TestIds.liveStream.copyPublicUrlButton}
                        onClick={handleCopyLiveStreamURL}
                        className='cursor-pointer border-none text-primary-blue'>
                        {t('copyLivestreamURL')}
                      </div>
                    </>
                  )}
                </div>
              </div>
              {!isMobileView && (
                <div
                  data-testid={TestIds.liveStream.recordingSection}
                  className='relative flex size-full flex-col'>
                  <div
                    className={`${
                      (!streamEvent || !streamEvent?.enabled) &&
                      'absolute inset-0 z-10 w-[105%] rounded-lg backdrop-blur-[3px]'
                    }`}
                  />
                  <div className='flex flex-row justify-between'>
                    <Label text={t('recording')} type={LABEL_TYPE.H4_BOLD} className='mb-3 mt-5' />
                    <ToggleSwitch
                      data-testid={TestIds.liveStream.recordingToggle}
                      containerClass='mt-5'
                      handleToggle={changeIsRecordingEnabled}
                      toggled={isRecordingEnabled}
                    />
                  </div>
                  {isRecordingEnabled && (
                    <div
                      data-testid={TestIds.liveStream.recordingDateInput}
                      className='flex flex-row justify-between'>
                      <Label text={t('availableUntil')} type={LABEL_TYPE.PARAGRAPH1} />
                      <FormikProvider value={formikRecording}>
                        <Formik
                          initialValues={formikRecording.initialValues}
                          onSubmit={() => undefined}>
                          {({ setFieldValue, values, errors }) => (
                            <DateInput
                              id='recordingDate'
                              label={''}
                              selected={values.recordingDate}
                              onChange={(date: Date) => {
                                setRecordingDate(date);
                                setFieldValue('recordingDate', date);
                              }}
                              errorMessage={errors.recordingDate as string}
                              isRequired={false}
                              shouldPickTime
                              dateFormat={dateAndHoursFormat}
                              minDate={new Date()}
                              onCalendarClose={onDateInputClose}
                            />
                          )}
                        </Formik>
                      </FormikProvider>
                    </div>
                  )}
                  <div
                    data-testid={TestIds.liveStream.waitingScreenSection}
                    className='my-1 w-full rounded-lg border border-secondary-blue-gray'>
                    <div className='mx-5'>
                      <div className='flex flex-row items-center justify-between'>
                        <Label
                          text={t('waitingScreen')}
                          type={LABEL_TYPE.H4_BOLD}
                          className='mb-6 mt-5'
                        />
                        <div
                          data-testid={TestIds.liveStream.waitingScreenEditDescriptionButton}
                          onClick={() => handleOpenDescriptionModal()}
                          className='flex !w-[140px] flex-row items-center gap-2 border-none'>
                          <EditPenSmallIcon className='h-4 w-[17px] fill-accent-dark-gray' />
                          <Label
                            text={t('editDescription')}
                            type={LABEL_TYPE.PARAGRAPH1}
                            className='!text-accent-dark-gray'
                          />
                        </div>
                      </div>
                      <Label
                        text={description}
                        type={LABEL_TYPE.PARAGRAPH2}
                        className='!text-secondary-gray'
                      />
                      {(hasContentManagementAccess || hasCoverPhotoCreationAccess) && (
                        <ClickOutsideWrapper
                          onClickOutside={() => setIsMenuOpen(false)}
                          className='ml-auto mt-5 w-full'>
                          <SingleSelect
                            id='backgroundImage'
                            testId={TestIds.liveStream.waitingScreenChangeBackgroundSelect}
                            value={null}
                            placeholder={t('changeBackgroundImage')}
                            options={
                              getUploadOptions(
                                hasContentManagementAccess,
                                hasCoverPhotoCreationAccess
                              ) as unknown as SelectOptionProps[]
                            }
                            handleChange={handleChange}
                            onMenuOpen={() => setIsMenuOpen(true)}
                            isLiveStream
                            menuIsOpen={isMenuOpen}
                          />
                        </ClickOutsideWrapper>
                      )}
                      <WaitingScreen
                        landingAssetUrl={streamEvent?.landingAsset?.assetUrl}
                        handleImageClick={handleImageClick}
                        description={description}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
            {isModalOpen && openedModal && (
              <Modal
                modalClasses={`min-h-[30%] 3xl:min-h-[20%] 4xl:min-h-[20%] w-full md:max-w-[55%] md:min-w-[35%] 3xl:min-w-[25%] 4xl:min-w-[25%] ${
                  dataMapping[openedModal]?.length &&
                  dataMapping[openedModal]?.length <= 3 &&
                  'md:w-[300px] 3xl:w-[600px] 4xl:w-[600px]'
                }`}
                height='h-full'
                isOpen={isModalOpen}
                label={getLabelMapping(t)[openedModal]}
                description={getDescriptionMapping(t)[openedModal]}
                hasButton={openedModal !== LiveStreamModalType.WAITING_SCREEN}
                buttonClasses={`flex justify-end ${
                  openedModal === LiveStreamModalType.TURN_ON_LIVESTREAM && 'justify-center mt-0'
                } ${openedModal === LiveStreamModalType.DESCRIPTION && 'mt-0'}`}
                buttonType={buttonTypeMapping[openedModal]}
                buttonDataTestId={TestIds.liveStream.modalConfirmButton}
                buttonText={
                  openedModal === LiveStreamModalType.DESCRIPTION ? t('saveChanges') : t('confirm')
                }
                subtext={
                  openedModal === LiveStreamModalType.TURN_ON_LIVESTREAM
                    ? t('enablingTheLiveStream')
                    : undefined
                }
                subtextClasses={
                  openedModal === LiveStreamModalType.TURN_ON_LIVESTREAM
                    ? 'font-medium text-center whitespace-pre-line max-w-[400px] mb-5'
                    : undefined
                }
                centered={openedModal === LiveStreamModalType.TURN_ON_LIVESTREAM}
                descriptionClasses={
                  openedModal === LiveStreamModalType.TURN_ON_LIVESTREAM
                    ? '!text-primary-black text-xl font-semibold text-center whitespace-pre-line max-w-[300px]'
                    : undefined
                }
                onClose={onCloseModal}
                handleSubmit={handleSubmitModal}
                isButtonDisabled={
                  !selectedAsset &&
                  (openedModal === LiveStreamModalType.UPLOADED_CONTENT ||
                    openedModal === LiveStreamModalType.COVER_PHOTO)
                }>
                {openedModal !== null && (
                  <LiveStreamModalContent
                    openedModal={openedModal}
                    dataMapping={dataMapping}
                    landingAssetUrl={streamEvent?.landingAsset?.assetUrl ?? ''}
                    formikDescription={formikDescription}
                    handleImageClick={handleImageClick}
                    description={description}
                    setSelectedAsset={setSelectedAsset}
                    selectedAsset={selectedAsset}
                  />
                )}
              </Modal>
            )}
            <ConfirmationModal
              isOpen={showRestartStreamModal}
              onClose={closeRestartStreamModal}
              onConfirm={() => {
                handleRestartStream();
                closeRestartStreamModal();
              }}
              message='resetStreamConfirm'
            />
          </div>
          {!hasLiveStreamingAccess && (
            <div
              data-testid={TestIds.liveStream.permissionModal}
              className='absolute inset-0 z-10 backdrop-blur-sm'>
              <Modal
                isOpen={!hasLiveStreamingAccess}
                modalClasses='md:w-[450px] w-full border border-secondary-blue-gray'
                hasCloseIcon={false}
                onClose={() => {
                  undefined;
                }}>
                <div className='flex w-full flex-col items-center justify-center gap-y-4 text-center'>
                  <Label type={LABEL_TYPE.PARAGRAPH1_BOLD} text={t('featureUnavailable')} />
                  <Label
                    type={LABEL_TYPE.PARAGRAPH2}
                    text={t('yourOranizationDoesntHavePermission', {
                      permission: t(`${ORGANIZATION_PERMISSION_TYPE.LIVE_STREAMING}`)
                    })}
                  />
                </div>
              </Modal>
            </div>
          )}
        </div>
      )}
    </>
  );
};
